import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher } from '@remix-run/react';
import { atom, useAtom } from 'jotai';
import { useDebounceFn } from 'ahooks';
import type { PageInfo } from 'generated/graphql';
import {
    CONTACT_SUGGESTIONS_API_ROUTE,
    type loader,
} from '~/routes/remix-api.chat.contact-suggestions';
import { useUserLoggedIn } from '~/modules/user';
import type { ContactSuggestion } from '../types';

/**
 * The cached contact suggestions and pagination information for the current
 * user with no filters applied.
 */
const contactSuggestionsCacheAtom = atom<{
    suggestions: ContactSuggestion[];
    pageInfo?: PageInfo;
    offset: number;
}>({ suggestions: [], offset: 0 });

/**
 * Hook to get contact suggestions for the current user.
 * @param limit The maximum number of suggestions to return. Do not change this
 *  value. Defaults to 50.
 */
export function useContactSuggestions({
    limit = 50,
}: {
    limit?: number;
} = {}) {
    const { isLoggedIn } = useUserLoggedIn();
    const [cache, setCache] = useAtom(contactSuggestionsCacheAtom);
    const isFetched = useRef(false);
    const [search, setSearch] = useState('');
    const [excludeMemberIDs, setExcludeMemberIDs] = useState<number[]>([]);
    const [offset, setOffset] = useState(cache.offset);
    const [suggestions, setSuggestions] = useState<ContactSuggestion[]>(
        cache.suggestions
    );
    const [pageInfo, setPageInfo] = useState<PageInfo | undefined>(
        cache.pageInfo
    );
    const fetcher = useFetcher<typeof loader>();
    const apiUrl = useMemo(() => {
        const url = new URL(
            CONTACT_SUGGESTIONS_API_ROUTE,
            window.location.origin
        );
        url.searchParams.set('q', search);
        url.searchParams.set('limit', String(limit));
        url.searchParams.set('offset', String(offset));
        for (const excludeMemberID of excludeMemberIDs) {
            url.searchParams.append(
                'excludeMemberIDs',
                String(excludeMemberID)
            );
        }
        return `${url.pathname}${url.search}`;
    }, [search, excludeMemberIDs, limit, offset]);

    const { run: setSearchDebounced } = useDebounceFn(
        (newSearch: string, offset: number, excludeMemberIDs?: number[]) => {
            setSearch(newSearch);
            setOffset(offset);
            if (excludeMemberIDs) {
                setExcludeMemberIDs(excludeMemberIDs);
            }
        },
        { wait: 300 }
    );

    const setSearchCallback = useCallback(
        (newSearch: string, excludeMemberIDs?: number[]) => {
            newSearch = newSearch.trim();
            setSearchDebounced(
                newSearch,
                // Reset offset when search changes (use cached value for no search).
                newSearch ? 0 : cache.offset,
                excludeMemberIDs
            );
            if (!newSearch) {
                setSuggestions(cache.suggestions);
                setPageInfo(cache.pageInfo);
            }
        },
        [cache, setSearchDebounced]
    );

    const loadMore = useCallback(() => {
        if (fetcher.state !== 'idle' || !pageInfo?.hasNextPage) {
            return;
        }
        setOffset(suggestions.length);
    }, [pageInfo, fetcher.state, suggestions.length]);

    useEffect(() => {
        if (!fetcher.data && fetcher.state === 'idle' && !isFetched.current) {
            // Fetch initial suggestions on mount.
            fetcher.load(apiUrl);
            isFetched.current = true;
        } else if (isFetched.current) {
            // Refetch when the query params change.
            fetcher.load(apiUrl);
            // Reset fetcher.data to avoid caching the filtered suggestions.
            fetcher.data = undefined;
        }
        // Must not include `fetcher` to avoid infinite loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiUrl]);

    // Set data from fetcher and cache results.
    useEffect(() => {
        if (!fetcher.data || fetcher.state !== 'idle') {
            return;
        }
        const newSuggestions = fetcher.data.suggestions.nodes;
        const newPageInfo = fetcher.data.suggestions.pageInfo;
        setSuggestions((prev) => {
            let next = newSuggestions;
            if (newPageInfo.hasPreviousPage && newSuggestions.length > 0) {
                // Append next page to results, replacing duplicates.
                const nextPageStartID = newSuggestions[0]!.ID;
                const existingIndex = prev.findIndex(
                    (suggestion) => suggestion.ID === nextPageStartID
                );
                if (existingIndex !== -1) {
                    next = [...prev.slice(0, existingIndex), ...newSuggestions];
                } else {
                    next = [...prev, ...newSuggestions];
                }
            }
            if (!search.trim()) {
                setCache({ suggestions: next, pageInfo: newPageInfo, offset });
            }
            return next;
        });
        setPageInfo(newPageInfo);
    }, [search, offset, fetcher, setCache]);

    // Clear the contact suggestions cache when the user logs out.
    useEffect(() => {
        if (!isLoggedIn) {
            setCache({ suggestions: [], offset: 0 });
        }
    }, [isLoggedIn, setCache]);

    return {
        /**
         * The list of contact suggestions.
         */
        suggestions,
        /**
         * There are more contact suggestions to load.
         */
        hasMore: pageInfo?.hasNextPage ?? false,
        /**
         * The suggestions are currently being loaded.
         */
        isLoading: fetcher.state !== 'idle',
        /**
         * Set the search value to filter the suggestion results. Optionally
         * include a list of member IDs to exclude from the results. Changing
         * the search params will trigger a call to the API after a short delay (debounced).
         */
        setSearch: setSearchCallback,
        /**
         * Load more suggestions by fetching the next page. This increments the
         * offset and appends the new results to the existing suggestions.
         */
        loadMore,
    };
}
